import { toDate } from "@/lib/format";

// Business urgency: critical = money/customer at risk now, high = act today, medium = review, low = FYI.
export const URGENCY = {
  critical: { label: "Critical", rank: 0, cls: "bg-red-600 text-white", dot: "bg-red-600" },
  high: { label: "High", rank: 1, cls: "bg-orange-100 text-orange-700 border border-orange-200", dot: "bg-orange-500" },
  medium: { label: "Medium", rank: 2, cls: "bg-amber-50 text-amber-700 border border-amber-200", dot: "bg-amber-400" },
  low: { label: "Low", rank: 3, cls: "bg-slate-100 text-slate-600 border border-slate-200", dot: "bg-slate-400" },
};

export const CATEGORIES = {
  leads: { label: "Leads", section: "requests", path: "/requests", icon: "Send" },
  orders: { label: "Orders", section: "orders", path: "/orders", icon: "Package" },
  promise: { label: "Brugo Promise", section: "promise", path: "/promise", icon: "ShieldCheck" },
  care: { label: "Customer Care", section: "care", path: "/care", icon: "Headset" },
  pros: { label: "Pro joining & approvals", section: "professionals", path: "/professionals", icon: "UserPlus" },
  projects: { label: "Projects & milestones", section: "projects", path: "/projects", icon: "FolderKanban" },
  payments: { label: "Payments", section: "payments", path: "/payments", icon: "IndianRupee" },
  visits: { label: "Site visits", section: "visits", path: "/visits", icon: "ClipboardCheck" },
};

// Extra sidebar sections an event also lights up (e.g. a milestone shows in Super CRM AND Projects).
const ALSO = {
  milestone_plans: ["promise"],
  supervisor_visits: ["care"],
  critical_notifications: ["care"],
};
export const sectionsOf = (e) => [CATEGORIES[e.cat].section, ...((e.coll === "supervisor_visits" && e.urgency === "low") ? [] : (ALSO[e.coll] || []))];

const WINDOW_MS = 30 * 864e5;
const ms = (v) => toDate(v)?.getTime() || 0;
const lower = (v) => String(v || "").toLowerCase();

const ev = (cat, coll, d, urgency, title, sub, isNewOverride) => {
  const created = ms(d.created_at || d.server_ts || d.order_placed_at);
  const ts = Math.max(created, ms(d.updated_at), ms(d.status_changed_at), ms(d.last_message_at), ms(d.paid_at),
    ms(d.logged_at), ms(d.customer_accepted_at), ms(d.customer_decision_at), ms(d.ready_for_review_at), ms(d.customer_approved_at),
    ms(d.actual_start_at), ms(d.actual_end_at), ms(d.activated_at), ms(d.submitted_at), ms(d.resubmitted_at));
  const isNew = isNewOverride ?? (ts - created < 60_000);
  return { key: `${coll}/${d.id}`, id: d.id, coll, cat, urgency, title, sub, ts, kind: isNew ? "New" : "Updated", by: d.cc_updated_by || d.manual_updated_by || null };
};

const lastMsgFrom = (d) => (Array.isArray(d.messages) && d.messages.length ? d.messages[d.messages.length - 1].from : null);

export const BUILDERS = {
  requests: (d) => {
    const ops = d.internal_status === "ops_required";
    return ev("leads", "requests", d, ops ? "critical" : "high", `Lead ${d.lead_code || d.id.slice(-6).toUpperCase()} · ${d.subcategory || d.category || (d.services || []).join(", ") || "Service"}`, ops ? "Ops required — no pro has picked it up" : `${d.city || ""} · ${d.customer_status || d.internal_status || "new"}`);
  },
  orders: (d) => {
    const st = String(d.current_status || d.status || "").replace(/_/g, " ");
    const issue = d.return_request || d.cancellation || lower(st).includes("cancel");
    return ev("orders", "orders", d, issue ? "high" : lower(st) === "order placed" ? "high" : "medium", `Order ${d.order_code || d.id.slice(-6)} · ₹${d.total ?? d.payable ?? ""}`, issue ? "Cancellation / return requested" : `Status: ${st || "—"}`);
  },
  promise_requests: (d) => {
    const st = lower(d.status || "submitted");
    const pending = ["submitted", "interested", "pending", "requested", "new", ""].includes(st);
    return ev("promise", "promise_requests", d, d.payment_mismatch ? "critical" : pending ? "high" : st === "activated" ? "low" : "medium", `Promise request ${d.project_code || d.request_id || d.id}`, `Status: ${st.replace(/_/g, " ")}${d.fee ? ` · fee ₹${d.fee}` : ""}`);
  },
  support_conversations: (d) => {
    const open = !["resolved", "closed"].includes(lower(d.status));
    const waiting = open && lastMsgFrom(d) !== "brugo_support";
    return ev("care", "support_conversations", d, waiting ? (lower(d.priority) === "urgent" ? "critical" : "high") : "low", `Pro ticket · ${d.subject || "Support"}`, `${d.business_name || d.professional_id || ""} · ${waiting ? "awaiting our reply" : d.status}`);
  },
  support_tickets: (d) => {
    const open = !["resolved", "closed"].includes(lower(d.status));
    const waiting = open && lastMsgFrom(d) !== "brugo_support";
    const u = ["urgent", "high"].includes(lower(d.priority)) && open ? "critical" : waiting ? "high" : "low";
    return ev("care", "support_tickets", d, u, `${d.stakeholder_type || "Stakeholder"} ticket · ${d.subject || ""}`, `${d.stakeholder_name || d.stakeholder_phone || ""} · ${d.status}`);
  },
  supervisor_visits: (d) => {
    const bad = !["normal", "", "ok", "good"].includes(lower(d.severity));
    return ev("visits", "supervisor_visits", d, bad ? (["critical", "high", "major"].includes(lower(d.severity)) ? "critical" : "high") : "low",
      bad ? `Site issue · ${d.severity}` : `Supervisor visit · ${d.supervisor_name || ""}`, d.remark || d.project_id);
  },
  critical_notifications: (d) => ev("care", "critical_notifications", d, lower(d.status) === "unread" ? "critical" : "medium", `Supervisor concern · ${d.title || d.project_id || ""}`, d.message || ""),
  milestone_plans: (d) => {
    const st = lower(d.status);
    const u = ["change_requested", "declined", "pending_brugo_signature"].includes(st) ? "critical" : st === "active" ? "low" : "high";
    return ev("projects", "milestone_plans", d, u, `Milestone plan · ${d.project_number || d.project_id || d.id}`, `Status: ${st.replace(/_/g, " ") || "—"}`);
  },
  chat_threads: (d) => ev("care", "chat_threads", d, "low", `Chat · ${d.title || d.request_id || d.id}`, d.last_message_preview || "", false),
  pro_logins: (d) => ev("pros", "pro_logins", { ...d, created_at: d.attempted_at }, d.outcome === "failed" ? "medium" : "low", `Pro login ${d.outcome === "failed" ? "failed" : d.outcome === "success" ? "success" : "attempt"} · ${d.phone || ""}`, `${d.device || ""}${d.failure_reason ? ` · ${d.failure_reason}` : ""}`, true),
  pro_signups: (d) => ev("pros", "pro_signups", d, lower(d.status) === "approved" ? "low" : "high", `Pro joining request · ${d.full_name || d.phone}`, `${d.trade || (d.categories || []).join(", ")} · ${d.status || "new"}`),
  professionals: (d) => {
    const sub = d.onboarding_status === "submitted";
    return ev("pros", "professionals", d, sub ? "high" : "low", `${sub ? "Approval pending" : "Onboarding"} · ${d.full_name || d.business_name || d.id}`, `${d.category_name || ""} · ${d.onboarding_status || d.status || ""}`);
  },
  projects: (d) => ev("projects", "projects", d, "medium", `Project ${d.project_number || d.title || d.id}`, `Status: ${d.status || "—"}`),
  project_milestones: (d) => ev("projects", "project_milestones", d, d.status === "disputed" || d.payment_overdue ? "critical" : d.payment_status === "due" ? "high" : ["ready_for_review", "under_review"].includes(d.status) ? "high" : "medium", `Milestone · ${d.title || d.id}`, `Status: ${d.status || "—"}${d.payment_status ? ` · payment ${d.payment_status}` : ""}`),
  razorpay_payments: (d) => {
    const st = lower(d.status);
    return ev("payments", "razorpay_payments", d, st === "failed" ? "critical" : st === "paid" ? "medium" : "low", `Payment ₹${(Number(d.amount) || 0) / 100} · ${d.purpose || ""}`, `${d.status} · ${d.method || d.paid_via || ""} · ${d.mode || ""}`);
  },
  wallet_payments: (d) => ev("payments", "wallet_payments", d, "medium", `Wallet payment ₹${d.wallet_used ?? d.bill_total ?? ""} · ${d.purpose || ""}`, d.status),
};

export const WATCHED = Object.keys(BUILDERS);

export function buildEvents(data) {
  const cutoff = Date.now() - WINDOW_MS;
  const out = [];
  WATCHED.forEach((c) => (data[c] || []).forEach((d) => {
    const e = BUILDERS[c](d);
    if (e && e.ts >= cutoff) out.push(e);
  }));
  return out.sort((a, b) => b.ts - a.ts);
}
